import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Trash2, Save, Loader2, EyeOff, Eye, Sliders, Check, Copy, FileText } from "lucide-react";
import { ISSUE_CATEGORIES } from "@/lib/issueCategories";
import FilterMultiSelect, { SearchSelect } from "@/components/filters/FilterMultiSelect";

const KIND_OPTS = [
  { k: "probleme", label: "Problématique" },
  { k: "demande", label: "Demande" },
  { k: "both", label: "Les deux" },
];

const TRIGGER_OPTS = [
  { k: "", label: "Aucun" },
  { k: "recrutement", label: "Bloc recrutement" },
  { k: "tenue", label: "Bloc tenue" },
  { k: "disciplinaire", label: "Bloc disciplinaire" },
  { k: "stickers", label: "Bloc stickers" },
  { k: "produit_ha", label: "Bloc produit rupture" },
  { k: "produit_nc", label: "Bloc produit non conforme (2 photos)" },
  { k: "arret_maladie", label: "Bloc arrêt maladie" },
];

export default function AdminForms() {
  const [templates, setTemplates] = useState([]);
  const [current, setCurrent] = useState(null);       // template being edited
  const [saving, setSaving] = useState(false);
  const [selectedCat, setSelectedCat] = useState(Object.keys(ISSUE_CATEGORIES)[0]);
  const [newSub, setNewSub] = useState({ k: "", kind: "probleme", photo_required: false, trigger: "" });
  const [newTplName, setNewTplName] = useState("");

  const load = async () => {
    try {
      const { data } = await api.get("/issue-templates");
      setTemplates(data);
      if (!current || !data.find(t => t.id === current.id)) {
        setCurrent(data.find(t => t.is_active) || data[0] || null);
      } else {
        setCurrent(data.find(t => t.id === current.id));
      }
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!current) return;
    setSaving(true);
    try {
      await api.put(`/issue-templates/${current.id}`, {
        name: current.name,
        custom_subcategories: current.custom_subcategories || {},
        disabled_subcategories: current.disabled_subcategories || [],
      });
      toast.success("Template enregistré");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    setSaving(false);
  };

  const createTpl = async () => {
    if (!newTplName.trim()) return toast.error("Nom du template requis");
    try {
      const { data } = await api.post("/issue-templates", { name: newTplName.trim(), custom_subcategories: {}, disabled_subcategories: [] });
      toast.success("Template créé");
      setNewTplName(""); setCurrent(data); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const duplicate = async () => {
    if (!current) return;
    try {
      const { data } = await api.post("/issue-templates", {
        name: `${current.name} (copie)`,
        custom_subcategories: current.custom_subcategories || {},
        disabled_subcategories: current.disabled_subcategories || [],
      });
      toast.success("Template dupliqué"); setCurrent(data); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const activate = async (t) => {
    try {
      await api.post(`/issue-templates/${t.id}/activate`);
      toast.success(`« ${t.name} » activé`);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const removeTpl = async (t) => {
    if (t.is_active) return toast.error("Impossible de supprimer le template actif");
    if (!window.confirm(`Supprimer « ${t.name} » ?`)) return;
    try { await api.delete(`/issue-templates/${t.id}`); toast.success("Supprimé"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  if (!current) {
    return (
      <div className="space-y-4">
        <h1 className="font-display text-3xl font-bold">Éditeur de formulaires</h1>
        <div className="bg-white border border-slate-200 rounded-2xl p-8 text-center text-slate-500">
          <FileText className="w-8 h-8 mx-auto mb-2 text-slate-400"/>
          Aucun template. Créez-en un pour démarrer.
          <div className="mt-4 flex gap-2 justify-center">
            <input value={newTplName} onChange={e => setNewTplName(e.target.value)} placeholder="Nom du template" className="px-3 py-2 rounded-lg border border-slate-200 text-sm"/>
            <button onClick={createTpl} className="px-4 py-2 rounded-lg bg-blue-600 text-white text-sm font-semibold">Créer</button>
          </div>
        </div>
      </div>
    );
  }

  const cat = ISSUE_CATEGORIES[selectedCat];
  const customs = (current.custom_subcategories || {})[selectedCat] || [];

  const updateCurrent = (patch) => setCurrent(c => ({ ...c, ...patch }));

  const addCustom = () => {
    if (!newSub.k.trim()) return toast.error("Nom requis");
    const key = newSub.k.trim();
    if ([...cat.sub, ...customs].some(s => s.k === key)) return toast.error("Ce libellé existe déjà");
    const next = { ...(current.custom_subcategories || {}) };
    next[selectedCat] = [...customs, { ...newSub, k: key, trigger: newSub.trigger || undefined }];
    updateCurrent({ custom_subcategories: next });
    setNewSub({ k: "", kind: "probleme", photo_required: false, trigger: "" });
  };
  const removeCustom = (k) => {
    const next = { ...(current.custom_subcategories || {}) };
    next[selectedCat] = customs.filter(s => s.k !== k);
    updateCurrent({ custom_subcategories: next });
  };
  const toggleDisabled = (k) => {
    const d = current.disabled_subcategories || [];
    updateCurrent({ disabled_subcategories: d.includes(k) ? d.filter(x => x !== k) : [...d, k] });
  };

  return (
    <div className="space-y-4" data-testid="admin-forms-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-display text-3xl font-bold tracking-tight text-slate-900">Éditeur de formulaires</h1>
          <p className="text-slate-500 mt-1 text-sm">Gérez plusieurs templates (été, hiver, promo…). Un seul actif à la fois.</p>
        </div>
      </div>

      {/* Templates bar */}
      <div className="bg-white border border-slate-200 rounded-2xl p-3" data-testid="templates-bar">
        <div className="flex items-center gap-2 flex-wrap">
          <div className="text-xs font-bold uppercase text-slate-500 pr-2">Templates :</div>
          {templates.map(t => (
            <div key={t.id} className={`flex items-center gap-1 px-2 py-1 rounded-lg border ${t.id === current.id ? "border-blue-500 bg-blue-50" : "border-slate-200"}`}>
              <button data-testid={`select-tpl-${t.id}`} onClick={() => setCurrent(t)}
                      className={`text-xs font-semibold ${t.is_active ? "text-emerald-700" : "text-slate-700"} flex items-center gap-1`}>
                {t.is_active && <Check className="w-3 h-3 text-emerald-600"/>}{t.name}
              </button>
              {!t.is_active && (
                <>
                  <button onClick={() => activate(t)} data-testid={`activate-tpl-${t.id}`}
                          className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-emerald-600 text-white hover:bg-emerald-700">Activer</button>
                  <button onClick={() => removeTpl(t)} className="text-rose-500 hover:bg-rose-50 rounded p-0.5"><Trash2 className="w-3 h-3"/></button>
                </>
              )}
            </div>
          ))}
          <div className="flex-1"/>
          <input value={newTplName} onChange={e => setNewTplName(e.target.value)} data-testid="new-tpl-name"
                 placeholder="Nom nouveau template" className="px-2 py-1 rounded-lg border border-slate-200 text-xs"/>
          <button onClick={createTpl} data-testid="create-tpl"
                  className="text-xs font-bold px-2 py-1 rounded-lg bg-slate-900 hover:bg-slate-800 text-white flex items-center gap-1">
            <Plus className="w-3 h-3"/> Nouveau
          </button>
          <button onClick={duplicate} className="text-xs font-bold px-2 py-1 rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-700 flex items-center gap-1">
            <Copy className="w-3 h-3"/> Dupliquer
          </button>
        </div>
      </div>

      {/* Current template editor */}
      <div className="flex items-center justify-between gap-3 bg-blue-50 border border-blue-200 rounded-2xl p-3">
        <div className="flex items-center gap-2">
          <FileText className="w-4 h-4 text-blue-600"/>
          <input value={current.name} onChange={e => updateCurrent({ name: e.target.value })} data-testid="current-tpl-name"
                 className="px-2 py-1 rounded-lg border border-blue-300 text-sm font-bold bg-white min-w-[240px]"/>
          {current.is_active && <span className="text-[10px] font-bold px-2 py-0.5 rounded bg-emerald-600 text-white">ACTIF</span>}
        </div>
        <button onClick={save} disabled={saving} data-testid="admin-forms-save"
                className="flex items-center gap-2 px-4 py-2 rounded-xl bg-blue-600 hover:bg-blue-700 text-white font-semibold text-sm disabled:opacity-60">
          {saving ? <Loader2 className="w-4 h-4 animate-spin"/> : <Save className="w-4 h-4"/>} Enregistrer
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
        <div className="bg-white border border-slate-200 rounded-2xl p-3 space-y-1 h-fit">
          <div className="text-[10px] uppercase font-bold text-slate-500 px-2 py-1">Catégories</div>
          {Object.entries(ISSUE_CATEGORIES).map(([k, c]) => (
            <button key={k} data-testid={`admin-cat-${k}`} onClick={() => setSelectedCat(k)}
                    className={`w-full text-left px-3 py-2 rounded-lg text-xs font-semibold ${selectedCat === k ? "bg-blue-600 text-white" : "text-slate-700 hover:bg-slate-50"}`}>
              {c.label}
              <span className={`ml-2 text-[10px] font-normal ${selectedCat === k ? "text-white/70" : "text-slate-400"}`}>
                {c.sub.length + ((current.custom_subcategories || {})[k] || []).length}
              </span>
            </button>
          ))}
        </div>

        <div className="lg:col-span-3 space-y-4">
          <div className="bg-white border border-slate-200 rounded-2xl p-5">
            <div className="flex items-center gap-2 mb-3">
              <Sliders className="w-4 h-4 text-slate-500"/>
              <h2 className="font-display text-lg font-semibold">{cat.label}</h2>
              <span className={`text-[10px] font-bold px-2 py-0.5 rounded border ${cat.color}`}>{selectedCat}</span>
            </div>

            <div className="bg-slate-50 border border-slate-200 rounded-xl p-3 mb-4">
              <div className="text-xs font-bold text-slate-600 mb-2">➕ Ajouter une sous-catégorie personnalisée</div>
              <div className="grid grid-cols-1 md:grid-cols-5 gap-2">
                <input value={newSub.k} onChange={e => setNewSub({...newSub, k: e.target.value})} data-testid="admin-new-sub-name"
                       placeholder="Ex: HA - Retour SAV" className="md:col-span-2 px-3 py-2 rounded-lg border border-slate-200 text-xs"/>
                <SearchSelect value={newSub.kind} onChange={v => setNewSub({...newSub, kind: v})} options={[...KIND_OPTS.map(o => ({ value: o.k, label: o.label }))]} size="lg" menuWidth={260}/>
                <SearchSelect value={newSub.trigger} onChange={v => setNewSub({...newSub, trigger: v})} options={[...TRIGGER_OPTS.map(o => ({ value: o.k, label: o.label }))]} size="lg" menuWidth={260}/>
                <button onClick={addCustom} data-testid="admin-add-sub"
                        className="flex items-center justify-center gap-1 px-3 py-2 rounded-lg bg-slate-900 hover:bg-slate-800 text-white text-xs font-bold">
                  <Plus className="w-3 h-3"/> Ajouter
                </button>
              </div>
              <label className="mt-2 flex items-center gap-1.5 text-xs text-slate-600 cursor-pointer">
                <input type="checkbox" checked={newSub.photo_required} onChange={e => setNewSub({...newSub, photo_required: e.target.checked})}/>
                Photo obligatoire
              </label>
            </div>

            <div className="text-[10px] uppercase font-bold text-slate-500 mb-2">Sous-catégories système ({cat.sub.length})</div>
            <div className="space-y-1 mb-4">
              {cat.sub.map(s => {
                const disabled = (current.disabled_subcategories || []).includes(s.k);
                return (
                  <div key={s.k} className={`flex items-center gap-2 p-2 rounded-lg border ${disabled ? "border-dashed border-slate-300 bg-slate-50 opacity-60" : "border-slate-200 bg-white"}`}>
                    <div className={`flex-1 text-xs ${disabled ? "line-through text-slate-500" : "text-slate-800"}`}>
                      {s.k}
                      {s.photo_required && <span className="ml-1 text-[10px] text-amber-600">📷</span>}
                      {s.trigger && <span className="ml-1 text-[10px] text-fuchsia-600">bloc: {s.trigger}</span>}
                      {s.kind && <span className="ml-1 text-[9px] uppercase font-bold text-slate-400">{s.kind}</span>}
                    </div>
                    <button data-testid={`admin-toggle-${s.k}`} onClick={() => toggleDisabled(s.k)}
                            className={`text-[10px] font-bold px-2 py-1 rounded flex items-center gap-1 ${disabled ? "bg-emerald-600 text-white" : "bg-slate-200 text-slate-700 hover:bg-slate-300"}`}>
                      {disabled ? <><Eye className="w-3 h-3"/> Réactiver</> : <><EyeOff className="w-3 h-3"/> Masquer</>}
                    </button>
                  </div>
                );
              })}
            </div>

            <div className="text-[10px] uppercase font-bold text-slate-500 mb-2">Sous-catégories personnalisées ({customs.length})</div>
            <div className="space-y-1">
              {customs.length === 0 && <div className="text-xs text-slate-400 italic py-4 text-center">Aucune sous-catégorie personnalisée.</div>}
              {customs.map(s => (
                <div key={s.k} className="flex items-center gap-2 p-2 rounded-lg border border-blue-200 bg-blue-50">
                  <div className="flex-1 text-xs text-slate-800">
                    <b>{s.k}</b>
                    {s.photo_required && <span className="ml-1 text-[10px] text-amber-600">📷</span>}
                    {s.trigger && <span className="ml-1 text-[10px] text-fuchsia-600">bloc: {s.trigger}</span>}
                    <span className="ml-1 text-[9px] uppercase font-bold text-slate-400">{s.kind}</span>
                  </div>
                  <button onClick={() => removeCustom(s.k)} className="text-rose-600 hover:bg-rose-100 rounded p-1"><Trash2 className="w-3.5 h-3.5"/></button>
                </div>
              ))}
            </div>
          </div>

          <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 text-xs text-amber-800">
            💡 Les modifications ne s'appliquent au terrain qu'une fois le template <b>Actif</b> et après avoir cliqué sur <b>Enregistrer</b>.
          </div>
        </div>
      </div>
    </div>
  );
}
